import {forwardRef, useCallback, useRef} from 'react';
import {Button} from '@ezcater/tapas';
import isChromatic from 'chromatic/isChromatic';
import useTranslation from 'next-translate/useTranslation';

import relishWallMp4 from '@/assets/images/home/relish-wall.mp4';
import relishWallPoster from '@/assets/images/home/relish-wall-poster.jpg';
import List from '@/components/ui/List';
import useTracking from '@/hooks/useTracking';
import {RECURRING_EMPLOYEE_MEALS_PATH} from '@/paths';

const MAX_LOOPS = 4;

const MealProgram = forwardRef<HTMLElement, unknown>(function MealProgram(_props, ref) {
  const {t} = useTranslation('home');
  const {trackClick} = useTracking();
  const loopCount = useRef(0);
  const bulletList = [t('mealProgram.bullet1'), t('mealProgram.bullet2'), t('mealProgram.bullet3')];

  const trackCtaClick = () => {
    trackClick('meal-program-cta');
  };

  const handleEnded = useCallback((e: React.SyntheticEvent<HTMLVideoElement>) => {
    loopCount.current += 1;
    if (loopCount.current < MAX_LOOPS) {
      e.currentTarget.play();
    }
  }, []);

  return (
    <section ref={ref}>
      <div
        className="min-h-[223px] overflow-hidden bg-cover bg-right tablet:relative tablet:min-h-[753px]"
        style={{backgroundImage: `url(${relishWallPoster.src})`}}
      >
        <video
          src={relishWallMp4}
          aria-label={t('rebrand.mealProgram.altImage')}
          className="inset-0 h-full w-full object-cover object-right tablet:absolute"
          autoPlay={!isChromatic()}
          muted
          playsInline
          poster={relishWallPoster.src}
          onEnded={handleEnded}
        />

        <div className="relative z-1 flex flex-col gap-6 bg-white p-6 pb-12 tablet:left-[17%] tablet:top-[197px] tablet:w-[468px] tablet:rounded-2xl tablet:p-12">
          <h2 className="text-[32px] font-bold leading-[38px]">{t('mealProgram.title')}</h2>
          <List items={bulletList} keyPrefix="meal-program-detail" className="text-lg" />
          <Button
            className="w-full text-center tablet:w-fit desktop:text-left"
            render={
              <a data-testid="meal-program-cta" href={RECURRING_EMPLOYEE_MEALS_PATH}>
                {t('mealProgram.cta')}
              </a>
            }
            onClick={trackCtaClick}
          />
        </div>
      </div>
    </section>
  );
});

export default MealProgram;
